import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export function ChipSelect({ 
  options, 
  value, 
  onChange, 
  multiple = false,
  columns = 2,
  size = "default" // "default" | "compact"
}) {
  const handleSelect = (optionValue) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      if (optionValue === "none") {
        onChange(currentValues.includes("none") ? [] : ["none"]);
      } else {
        const withoutNone = currentValues.filter(v => v !== "none");
        if (currentValues.includes(optionValue)) {
          onChange(withoutNone.filter(v => v !== optionValue));
        } else {
          onChange([...withoutNone, optionValue]);
        }
      }
    } else {
      onChange(optionValue);
    }
  };

  const isSelected = (optionValue) => {
    if (multiple) {
      return Array.isArray(value) && value.includes(optionValue);
    }
    return value === optionValue;
  };

  const gridClass = columns === 2 
    ? "grid-cols-2" 
    : columns === 3 
    ? "grid-cols-3" 
    : columns === 4 
    ? "grid-cols-4"
    : "grid-cols-5";

  return (
    <div className={cn("grid gap-2", gridClass)}>
      {options.map((option) => {
        const selected = isSelected(option.value);
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            className={cn(
              "relative flex items-center justify-center gap-2 rounded-xl border transition-all duration-200",
              "active:scale-[0.97] hover:scale-[1.01]",
              size === "compact" ? "px-3 py-2 text-sm" : "px-4 py-3",
              selected
                ? "bg-cipher-green/15 border-cipher-green/50 text-white shadow-sm shadow-cipher-green/20"
                : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20"
            )}
            data-testid={`chip-${option.value}`}
          >
            {option.icon && <option.icon className="w-4 h-4" />}
            <span className="font-medium">{option.label}</span>
            {selected && (
              <Check className="w-4 h-4 text-cipher-green absolute right-2" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export function ChipSelectInline({ 
  options, 
  value, 
  onChange,
  size = "default"
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-full border transition-all duration-200",
              "active:scale-[0.97] hover:scale-[1.01]",
              size === "compact" ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm",
              selected
                ? "bg-cipher-green/15 border-cipher-green/50 text-white font-medium"
                : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:border-white/20"
            )}
            data-testid={`chip-inline-${option.value}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
